import { useCallback, useEffect, useRef } from 'react';
import type { Step } from '@de/types/types';
import type { DynamicExperienceEventContext } from '@de/events/buildEventContext';
import type { EventContext as PayloadEventContext } from '@de/utils/createEventingPayloadsDE';
import type { TrackingTriggers } from './TrackingProvider';

/**
 * Per-click context the host (`useHandleStepForward`) supplies when
 * the user advances the flow. Owned by the caller because only the
 * caller has the resolved-next-step / flow-state derivation in hand
 * at click time. The provider supplies the spec-compliant
 * orchestration over `eventContext` + `currentStep`; the caller
 * supplies the click-shape metadata that rides on `ProductClicked`
 * and the field array that rides on `FormContinued`.
 */
export interface ForwardStepClickContext {
  /**
   * Whether this click ejects the flow via redirectUrl (legacy DCS
   * `redirectUrl` or the DSL `discover` sentinel). When true on the entry step
   * the firer is the sole emitter of the entry's form event (`useHandleSubmit`
   * suppresses its `FormSubmitted`) and picks `FormSubmitted` vs `FormContinued`
   * via {@link isResultsLanding}. Non-entry exit-on-redirect suppresses `FormContinued`.
   */
  readonly isExitingStepWithRedirect: boolean;

  /**
   * Last-step flag — when true the navigation triggers `handleSubmit`
   * rather than advancing in-flow. The standard-path `FormSubmitted`
   * is still fired by `useHandleSubmit`; here we only suppress the
   * non-entry `FormContinued` so the last step does NOT double-emit
   * `FormContinued` + `FormSubmitted` from two firers.
   */
  readonly isLastStep: boolean;

  /**
   * Outbound URL surfaced on the entry's `ProductClicked` payload
   * (§6.3 / §6.8). `null` for inline (no-redirect) CTAs so Cohesion
   * consumers can distinguish "no outbound URL" from "explicitly
   * empty string". Ignored for non-entry steps.
   */
  readonly outboundUrl: string | null;

  /**
   * CTA label ridden on `ProductClicked` `customDimensions.ctaText`
   * (§6.7.2 / §6.8). Multi-CTA entry points use this to identify
   * which specific CTA the user clicked.
   */
  readonly ctaText: string;

  /**
   * Resolved `actionOutcome` for this specific click (§6.7.2 /
   * §6.7.3). `dexp-question-flow` when the click leads to another
   * question, `dexp-results` when it lands on the result page.
   * Resolved by the caller from the next step the user is about to
   * land on (or from the terminal flag for last-step submits).
   */
  readonly actionOutcome: string;

  // Whether this click lands directly on results — the structural source of
  // truth for the entry's `FormSubmitted` (true) vs `FormContinued` (false) split.
  readonly isResultsLanding: boolean;

  /**
   * DCS selections at click time, populated only for legacy
   * `StepType.DCS` entry steps without a DSL config. Non-DCS entries
   * omit this so `createProductClickedPayload` slugifies empty strings.
   *
   * Mutually exclusive with `productOverride` — when the entry step
   * ships a DSL config with `tracking.productFields`, the caller
   * passes `productOverride` instead.
   */
  readonly dcs?: {
    degree: string | undefined;
    category: string | undefined;
    subject: string | undefined;
  };

  /**
   * DSL-resolved product override map. Populated by
   * `useHandleStepForward` when the entry step's DSL config declares
   * a `tracking.productFields` block. Keys map 1:1 to wire
   * `ProductClicked.product.*` fields; values are resolved scalars
   * from the `flowContext` path the author declared.
   *
   * When set, takes precedence over `dcs` for canonical fields
   * (`variant`, `category`, `name`) and allows arbitrary additional
   * fields (e.g. `brand`) to flow onto the wire payload.
   */
  readonly productOverride?: Readonly<Record<string, string>>;

  /**
   * Resolved `tracking.customDimensions` overlay. Payload builders
   * merge this additively onto platform dims; same key from config
   * replaces the platform value.
   */
  readonly mappedCustomDimensions?: ReadonlyArray<{
    key: string;
    value: string;
  }>;

  /**
   * Product wire keys that did not resolve. The built-in entry
   * `ProductClicked` does not copy this list onto the wire. Configured
   * events name their own misses.
   */
  readonly productFieldFallbacks?: readonly string[];

  /**
   * Field array for the `FormContinued` payload (§6.5). Empty when
   * the step has no answered fields; the provider omits the `field`
   * key in that case.
   */
  readonly field: readonly {
    fieldName: string;
    fieldValue: string;
    fieldId?: string;
    fieldNumber?: number;
  }[];
}

/**
 * Module-level type for the firer the provider exposes. Surfaced as
 * a `useRef`-stable mutable cell that `DynamicExperienceProvider`
 * holds and `TrackingProvider`'s hook populates on mount. Lets
 * `useHandleStepForward` (which sits ABOVE `TrackingProvider` in the
 * tree and therefore cannot consume `useTracking()`) still delegate
 * forward-click event firing to the provider.
 */
export type ForwardStepEventFirerRef = React.MutableRefObject<
  ((ctx: ForwardStepClickContext) => void) | null
>;

export interface UseForwardStepEventFiringArgs {
  /**
   * Inherited event context from `DynamicExperienceProvider`. When
   * `undefined` (provider mounted outside `DynamicExperienceProvider`)
   * the installed firer silently returns, preserving the
   * `useTracking()` undefined-outside-provider contract for forward
   * clicks. Callers must still null-check `forwardStepEventFirerRef.current`
   * before invoking — it is `null` before `TrackingProvider`'s bridge
   * effect runs, after unmount, and when no `TrackingProvider` is
   * mounted at all.
   */
  readonly eventContext: DynamicExperienceEventContext | undefined;
  /** Current step from the surrounding DE context. */
  readonly currentStep: Step | undefined;
  /**
   * Current step index from the surrounding DE context — surfaced on
   * the `FormContinued` payload so each emitted event carries its
   * step number for funnel analysis.
   */
  readonly currentStepIndex: number | undefined;
  /** Trigger surface from `useFlowTrackingEvents`. */
  readonly triggers: TrackingTriggers;
  /**
   * Ref cell created by `DynamicExperienceProvider` and threaded
   * down to `useHandleStepForward`. The hook writes its firer into
   * `.current` on mount and clears it on unmount.
   */
  readonly forwardStepEventFirerRef: ForwardStepEventFirerRef | undefined;
  /**
   * Session-scoped `FormStarted` / `ProductClicked` single-fire gate,
   * owned by `DynamicExperienceProvider` (§6.1). Undefined when this
   * hook is mounted outside a DEP — in that case the firer falls back
   * to a local ref (still single-fire within the mount, no-op outside).
   */
  readonly formStartedFiredRef: React.MutableRefObject<boolean> | undefined;
  /**
   * Set synchronously by `createBoundRender`'s click dispatch when any
   * component's built-in `ProductClicked` was suppressed or replaced by a
   * configured click binding on the same click. This firer runs the
   * spec-compliant entry orchestration independently of that dispatch (it
   * is invoked from `useHandleStepForward`, which sits above
   * `TrackingProvider` and cannot see `createBoundRender`'s binding
   * decisions). On the entry step's first forward click it also sends
   * `ProductClicked`, so without this signal that component's click
   * double-sent: once from the binding, once from this firer. The
   * component can be any registered type whose click advances the step.
   *
   * Read-and-cleared on every entry click (not just when true) so a click
   * with no configured binding on the same control is unaffected. Form
   * events (`FormStarted` / `FormContinued` / `FormSubmitted`) are never
   * gated by this flag — only the built-in `ProductClicked` send is.
   */
  readonly suppressedProductClickRef?: React.MutableRefObject<boolean>;
}

export interface UseForwardStepEventFiringResult {
  /**
   * Spec-compliant forward-click firer. Exposed on
   * `TrackingContextValue` for direct test assertions and for any
   * DSL consumer that wants to drive the event sequence without
   * going through `useHandleStepForward`. Identical implementation
   * to the firer written into the ref bridge.
   */
  readonly fireForwardStepClick: (ctx: ForwardStepClickContext) => void;
}

/**
 * Owns the **forward-click** event sequence for the DE flow — fires
 * the spec-compliant subset of `FormStarted` / `ProductClicked` /
 * `FormContinued` per Confluence "Dynamic Experience Eventing
 * Requirements" §6.1 / §6.7.
 *
 * Orchestration rules:
 *
 * - **Entry step, first forward click** (§6.1 STEP 5 / §6.7.2):
 *   `FormStarted` → `ProductClicked` → (`FormContinued` |
 *   `FormSubmitted`), in that order, all fired synchronously inside
 *   the same handler so they land on the wire together.
 * - **Non-entry step forward click** (§6.8 STEP 6+): `FormContinued`
 *   only — no `ProductClicked` (the spec reserves `ProductClicked`
 *   for the entry CTA), no `FormStarted` (single-fire across the
 *   flow session). Suppressed entirely when the click is the last
 *   step (the standard-path `useHandleSubmit` fires `FormSubmitted`
 *   instead, preventing a duplicate `FormContinued` + `FormSubmitted`
 *   pair from the same click).
 * - **No-op surfaces**: when `eventContext` is undefined (provider
 *   mounted outside `DynamicExperienceProvider`) or `currentStep` is
 *   missing, the firer silently returns — preserves the
 *   `useTracking()` undefined-outside-provider contract for forward
 *   clicks.
 *
 * `FormStarted` / `ProductClicked` are session-scoped single-fire
 * (§6.1). The gate ref is owned by `DynamicExperienceProvider` and
 * bridged in via `formStartedFiredRef` so it survives this hook's
 * remount when `TrackingProvider` re-keys on step id. Falls back to
 * a local ref when no DEP is mounted.
 *
 * The same firing function is (1) returned on `result.fireForwardStepClick`
 * for direct consumers, and (2) written into the ref cell
 * `forwardStepEventFirerRef.current` so `useHandleStepForward` can
 * call it from above the `TrackingProvider` in the React tree.
 */
export const useForwardStepEventFiring = (
  args: UseForwardStepEventFiringArgs,
): UseForwardStepEventFiringResult => {
  const {
    eventContext,
    currentStep,
    currentStepIndex,
    triggers,
    forwardStepEventFirerRef,
    formStartedFiredRef: bridgedFormStartedFiredRef,
    suppressedProductClickRef,
  } = args;

  // Session-scoped single-fire gate. Use the DEP-supplied ref when
  // available; the local fallback covers the "mounted outside DEP"
  // path (firer is no-op anyway, but keeps types non-optional).
  const localFormStartedFiredRef = useRef(false);
  const formStartedFiredRef =
    bridgedFormStartedFiredRef ?? localFormStartedFiredRef;

  const fireForwardStepClick = useCallback(
    (ctx: ForwardStepClickContext): void => {
      // Mounted outside `DynamicExperienceProvider`, or no current
      // step — preserve the no-op contract.
      if (!eventContext || !currentStep) return;

      // `EventContext` (the payload-builder type used by the trigger
      // signatures) is structurally compatible with
      // `DynamicExperienceEventContext` (the canonical type owned by
      // `@de/events`). Cast at the boundary so the override spreads
      // type-check; the underlying value is the inherited context
      // from `DynamicExperienceProvider`.
      const baseEventContext = eventContext as unknown as PayloadEventContext;

      if (currentStep.isEntry && !formStartedFiredRef.current) {
        // §6.1 STEP 5 / §6.7: entry submission fires FormStarted +
        // ProductClicked + (FormContinued | FormSubmitted). We emit
        // them synchronously in the documented spec order so any
        // consumer that batches by tick sees them together.
        triggers.triggerFormStartedEvent(baseEventContext);
        // Read-and-clear on every entry click, not just when true, so a
        // click with no configured binding on the same control is
        // unaffected on the next entry render.
        const skipBuiltInProductClick =
          suppressedProductClickRef?.current === true;
        if (suppressedProductClickRef) {
          suppressedProductClickRef.current = false;
        }
        if (!skipBuiltInProductClick) {
          triggers.triggerProductClickedEvent({
            ...baseEventContext,
            outboundUrl: ctx.outboundUrl,
            // Prefer the DSL-resolved override map when present; fall
            // back to the legacy DCS triad. `createProductClickedPayload`
            // honors `product` first for canonical fields.
            ...(ctx.productOverride
              ? { product: { ...ctx.productOverride } }
              : {}),
            ...(ctx.dcs ? { dcs: ctx.dcs } : {}),
            actionOutcome: ctx.actionOutcome,
            customDimensions: [
              ...(baseEventContext.customDimensions || []),
              { key: 'ctaText', value: ctx.ctaText },
            ],
            ...(ctx.mappedCustomDimensions &&
            ctx.mappedCustomDimensions.length > 0
              ? { mappedCustomDimensions: [...ctx.mappedCustomDimensions] }
              : {}),
          });
        }
        // Entry form event is decided by where the click lands, not whether it
        // redirects: results → `FormSubmitted`, another question → `FormContinued`.
        // `useHandleSubmit` is suppressed for redirecting entries (and stays the
        // canonical emitter for non-redirect last-step submits), so this never
        // double-signals.
        if (ctx.isExitingStepWithRedirect || !ctx.isLastStep) {
          if (ctx.isResultsLanding) {
            triggers.triggerFormEvents('FormSubmitted', {
              ...baseEventContext,
              currentStep,
              currentStepIndex,
              field: ctx.field.length > 0 ? [...ctx.field] : undefined,
              outboundUrl: ctx.outboundUrl,
            } as PayloadEventContext);
          } else {
            triggers.triggerFormContinuedEvent({
              ...baseEventContext,
              currentStep,
              currentStepIndex,
              field: ctx.field.length > 0 ? [...ctx.field] : undefined,
            });
          }
        }
        formStartedFiredRef.current = true;
      } else if (!ctx.isLastStep && !ctx.isExitingStepWithRedirect) {
        // §6.8 STEP 6+: non-entry forward clicks fire FormContinued
        // only. Last-step clicks are owned by `useHandleSubmit`
        // (fires `FormSubmitted`); exit-on-redirect clicks suppress
        // the in-flow continuation signal for the same reason as the
        // entry branch above.
        triggers.triggerFormContinuedEvent({
          ...baseEventContext,
          currentStep,
          currentStepIndex,
          field: ctx.field.length > 0 ? [...ctx.field] : undefined,
        });
      }
    },
    [
      eventContext,
      currentStep,
      currentStepIndex,
      triggers,
      suppressedProductClickRef,
    ],
  );

  // Bridge: write the firer into the ref cell owned by
  // `DynamicExperienceProvider`. `useHandleStepForward` reads
  // `.current` at click time. Cleared on unmount so a stale firer
  // from a previous provider mount cannot be invoked.
  useEffect(() => {
    if (!forwardStepEventFirerRef) return;
    forwardStepEventFirerRef.current = fireForwardStepClick;
    return () => {
      if (forwardStepEventFirerRef.current === fireForwardStepClick) {
        forwardStepEventFirerRef.current = null;
      }
    };
  }, [forwardStepEventFirerRef, fireForwardStepClick]);

  return { fireForwardStepClick };
};
